/**
 * SVG Recoloring Utility
 * 
 * This module provides functions for recoloring SVG hotspots with caching support.
 * It's designed to work with krpano viewer and provides efficient caching to avoid
 * re-fetching and re-processing the same SVGs multiple times.
 * 
 * Usage:
 * import { recolorHotspotSvg, clearSvgCaches } from './svgRecolor.js';
 * 
 * // Recolor a hotspot SVG
 * recolorHotspotSvg('hotspotName', 'path/to/icon.svg', '#ff0000', krpanoInstance);
 * 
 * // Clear caches when needed
 * clearSvgCaches();
 */

// SVG cache for storing fetched and recolored SVGs
const svgCache = new Map();
const recoloredSvgCache = new Map();

/**
 * Generate a cache key for recolored SVGs
 * @param {string} srcUrl - The source URL of the SVG
 * @param {string} color - The color to apply
 * @returns {string} Cache key
 */
export function generateCacheKey(srcUrl, color) {
  return `${srcUrl}|${color}`;
}

/**
 * Recolor SVG content by removing existing fills and applying a new color
 * @param {string} svg - The SVG content as a string
 * @param {string} color - The color to apply (hex, rgb, or named color)
 * @returns {string} The recolored SVG content
 */
export function recolorSvg(svg, color) {
  // Remove existing fills (attributes or inline CSS)
  svg = svg.replace(/fill="[^"]*"/gi, '');
  svg = svg.replace(/fill:[^;"]*;?/gi, '');

  // Inject our fill
  svg = svg.replace(/<svg([^>]*)>/i, '<svg$1><g fill="' + color + '">')
           .replace(/<\/svg>/i, '</g></svg>');
  
  return svg;
}

/**
 * Create a data URI from SVG content
 * @param {string} svg - The SVG content as a string
 * @returns {string} Data URI for the SVG
 */
export function createDataUri(svg) {
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

/**
 * Clear all SVG caches
 */
export function clearSvgCaches() {
  svgCache.clear();
  recoloredSvgCache.clear();
  console.log('SVG caches cleared');
}

/**
 * Recolor a hotspot SVG and apply it to the krpano viewer
 * @param {string} hotspotName - The name of the hotspot
 * @param {string} srcUrl - The source URL of the SVG
 * @param {string} color - The color to apply
 * @param {Object} krpano - The krpano viewer instance
 */
export function recolorHotspotSvg(hotspotName, srcUrl, color, krpano) {
  console.log('recoloring hotspot', hotspotName, srcUrl, color);
  
  // replace %SWFPATH%
  srcUrl = srcUrl.replace('%SWFPATH%', '.');
  if (!krpano) { 
    console.error('krpano viewer not found'); 
    return; 
  }

  // Check if we already have this recolored SVG in cache
  const cacheKey = generateCacheKey(srcUrl, color);
  if (recoloredSvgCache.has(cacheKey)) {
    console.log('Using cached recolored SVG for', srcUrl, 'with color', color);
    const cachedDataUri = recoloredSvgCache.get(cacheKey);
    krpano.set('hotspot[' + hotspotName + '].url', cachedDataUri);
    return;
  }

  // Check if we have the original SVG in cache
  if (svgCache.has(srcUrl)) {
    console.log('Using cached original SVG for', srcUrl);
    const originalSvg = svgCache.get(srcUrl);
    const recoloredSvg = recolorSvg(originalSvg, color);
    const dataUri = createDataUri(recoloredSvg);
    
    // Cache the recolored version
    recoloredSvgCache.set(cacheKey, dataUri);
    
    krpano.set('hotspot[' + hotspotName + '].url', dataUri);
    return;
  }

  // Fetch the SVG if not in cache
  console.log('Fetching SVG from', srcUrl);
  fetch(srcUrl)
    .then(function(response) {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response.text();
    })
    .then(function(svg) {
      // Cache the original SVG
      svgCache.set(srcUrl, svg);
      // Recolor the SVG
      const recoloredSvg = recolorSvg(svg, color);
      const dataUri = createDataUri(recoloredSvg);
      // Cache the recolored version
      recoloredSvgCache.set(cacheKey, dataUri);
      // Apply to hotspot
      krpano.set('hotspot[' + hotspotName + '].url', dataUri);        
    })
    .catch(function(err) {
      console.error('SVG fetch/recolor failed:', err);
      // Fallback to original
      krpano.set('hotspot[' + hotspotName + '].url', srcUrl);
    });
}

// Export caches for external access if needed
export { svgCache, recoloredSvgCache };
